// Central image lookup — resolves page routes to their photography so
// cards across the site (challenges, related links, explorer modules)
// can show a relevant visual without duplicating image paths.
import { STRATEGY_SUBPAGES } from "./strategySubPages";
import { INTELLIGENCE_SUBPAGES } from "./intelligenceSubPages";
import { BRAND_SUBPAGES } from "./brandSubPages";
import { DIGITAL_SUBPAGES } from "./digitalSubPages";
import { INDUSTRIES } from "./industries";

export const CAP_PAGES = {
  "strategy-commercial-transformation": STRATEGY_SUBPAGES,
  "market-customer-intelligence": INTELLIGENCE_SUBPAGES,
  "brand-marketing-communications": BRAND_SUBPAGES,
  "digital-commerce": DIGITAL_SUBPAGES,
};

const CAP_IMAGES = {
  "strategy-commercial-transformation": "/photos/band-strategy.jpg",
  "market-customer-intelligence": "/photos/band-intelligence.jpg",
  "brand-marketing-communications": "/photos/band-brand.jpg",
  "digital-commerce": "/photos/band-digital.jpg",
};

const IMAGE_BY_PATH = (() => {
  const map = {};
  for (const [cap, pages] of Object.entries(CAP_PAGES)) {
    map[`/capabilities/${cap}`] = CAP_IMAGES[cap];
    for (const p of pages) {
      if (p.image) map[`/capabilities/${cap}/${p.slug}`] = p.image;
    }
  }
  for (const ind of INDUSTRIES) {
    if (ind.image) map[`/industries/${ind.slug}`] = ind.image;
  }
  return map;
})();

export const imageForPath = (to) => IMAGE_BY_PATH[to];

// Sibling service images of one capability, for per-card rotation.
export function serviceImagePool(capSlug, exclude) {
  return (CAP_PAGES[capSlug] || [])
    .map((p) => p.image)
    .filter((src) => src && src !== exclude);
}

// Other industries' images, for per-card rotation on industry pages.
export function industryImagePool(excludeSlug) {
  return INDUSTRIES.filter((i) => i.slug !== excludeSlug).map((i) => i.image);
}

// Industry-specific generated imagery (Commercial Reality cards + band).
export const industryChallengeImage = (slug, i) =>
  `/photos/ind-challenge-${slug}-${(i % 4) + 1}.jpg`;
export const industryBandImage = (slug) => `/photos/ind-band-${slug}.jpg`;

// Topic keyword → topical photograph, so challenge cards show an image that
// matches the challenge being discussed (falls back to a page-level pool).
const KEYWORD_IMAGES = [
  [/profit|margin|pricing?|discount|revenue|monetis|leakage/i, "/photos/sub-pricing-revenue-management.jpg"],
  [/sales|pipeline|prospecting|coverage|distributor/i, "/photos/sub-sales-transformation.jpg"],
  [/channel|route.to.market|retail execution|trade|partner-led/i, "/photos/sub-channel-route-to-market.jpg"],
  [/market entry|new market|enter|launch|geograph|expansion/i, "/photos/sub-go-to-market-market-entry.jpg"],
  [/position|differentiat|brand(?! architecture)|identity/i, "/photos/sub-brand-strategy-positioning.jpg"],
  [/brand architecture|naming|portfolio of brands/i, "/photos/sub-brand-architecture-portfolio.jpg"],
  [/advertis|creative|content market|campaign/i, "/photos/sub-advertising-content-creative.jpg"],
  [/paid|media|performance market|acquisition cost|cac\b/i, "/photos/sub-performance-marketing-paid-media.jpg"],
  [/marketing|communicat|roi|spend/i, "/photos/sub-marketing-strategy-integrated-communications.jpg"],
  [/e-?commerce|marketplace|online store/i, "/photos/sub-ecommerce-marketplace-growth.jpg"],
  [/d2c|digital|website|cro|app\b|online/i, "/photos/sub-d2c-cro-digital-experience.jpg"],
  [/journey|decision/i, "/photos/sub-customer-decision-journey-intelligence.jpg"],
  [/customer|consumer|buyer|insight|behaviour|behavior/i, "/photos/sub-customer-buyer-insights.jpg"],
  [/segment|target/i, "/photos/sub-segmentation-targeting.jpg"],
  [/portfolio|product|assortment|innovation/i, "/photos/sub-product-portfolio-strategy.jpg"],
  [/crm|retention|loyal|repeat/i, "/photos/sub-crm-retention-loyalty.jpg"],
  [/competit/i, "/photos/sub-competitive-intelligence.jpg"],
  [/seo|search/i, "/photos/sub-seo-content-ai-search.jpg"],
  [/social|influencer|creator/i, "/photos/sub-social-influencer-creator-marketing.jpg"],
  [/reputation|public relation|\bpr\b/i, "/photos/sub-public-relations-reputation.jpg"],
  [/sizing|opportunit|trend|foresight|demand|forecast|data|analytics/i, "/photos/sub-market-opportunity-sizing.jpg"],
  [/capab|talent|team|organisation|operating model|excellence|leadership/i, "/photos/sub-commercial-excellence-operating-model.jpg"],
  [/growth|stall|plateau|scale|ambition/i, "/photos/sub-growth-strategy.jpg"],
];

export function imageForChallenge(text, fallback) {
  const t = text || "";
  for (const [re, src] of KEYWORD_IMAGES) {
    if (re.test(t)) return src;
  }
  return fallback;
}

// Every route image in one flat pool — used as uniqueness fallback.
export const ALL_IMAGES = Object.values(IMAGE_BY_PATH);

/**
 * Per-page image uniqueness: returns a picker that never repeats an image.
 * `reserved` images (hero, band, fixed visuals) are marked used up front;
 * each pick returns the preferred (most relevant) image unless already used,
 * in which case the next unused pool image is taken.
 */
export function uniquePicker(reserved = [], pools = []) {
  const used = new Set(reserved.filter(Boolean));
  const flat = pools.flat().filter(Boolean);
  return (preferred) => {
    if (preferred && !used.has(preferred)) {
      used.add(preferred);
      return preferred;
    }
    const spare = flat.find((s) => !used.has(s));
    if (spare) {
      used.add(spare);
      return spare;
    }
    return preferred;
  };
}
